import { findAllEntriesInTable } from 'store/functions/inputFunctions.js';
import { STATIC_TABLE_SETTINGS } from 'store/reducers/constants/constants.js';
import { validateRow } from './validateRow.js';

export const editRowHelper = (state, uuid, columnName, tableName, newValue, replaceUniques=false) => {
  let newState = {...state};
  let item = newState['data'][tableName].find(x => x['UUID'] === uuid);

  if (STATIC_TABLE_SETTINGS[tableName]['dropdownSource'] && STATIC_TABLE_SETTINGS[tableName]['dropdownSource'][columnName]) {
    if (STATIC_TABLE_SETTINGS[tableName]['dataType'][columnName] === 'multi-dropdown') {
      if (!newValue) {
        newValue = [];
      } 
      else if (newValue.constructor !== Array) {
        newValue = [newValue];
      }
    } else {
      if (newValue && newValue.constructor === Array) {
        newValue = newValue[0];
      }
    }
  }

  const oldValue = item[columnName];
  if (item) {
    item[columnName] = newValue;
    // A travel cost typed or pasted over a calculated one no longer came from
    // that method: drop the field's method tag, so "Calculate missing" doesn't
    // describe a hand-entered value as, say, driving.
    if (tableName === 'travel_costs' && item.costMethod && Object.prototype.hasOwnProperty.call(item.costMethod, columnName)) {
      const { [columnName]: _dropped, ...rest } = item.costMethod;
      item.costMethod = rest;
    }
  }
  let rowIdx;
  for (let i = 0; i < newState['data'][tableName].length; i++) {
    if (newState['data'][tableName][i]['UUID'] === uuid) {
      rowIdx = i;
      break;
    }
  }
  
  newState = validateRow(newState, tableName, rowIdx, replaceUniques);
  if (tableName === 'locations') {
    if (columnName === 'locationName') {
      for (const trip of newState['data']['trips']) {
        if (trip['fromLocationId'] === uuid) {
          trip['fromLocationName'] = newValue;
        }
        if (trip['toLocationId'] === uuid) {
          trip['toLocationName'] = newValue;
        }
      }
      for (const travelCost of newState['data']['travel_costs']) {
        if (travelCost['fromLocationId'] === uuid) {
          travelCost['fromLocationName'] = newValue;
        }
        if (travelCost['toLocationId'] === uuid) {
          travelCost['toLocationName'] = newValue;
        }
      }
    }
  }

  if (tableName === 'trips') {
    // Resolve the linked location id from the (dropdown) name. The name can be
    // null/empty (clearColumn dispatches a null edit; a cell can be cleared) or
    // a not-yet-matching value, so guard the lookup — reading .UUID off an
    // unmatched find() used to throw and crash the edit.
    if (columnName === 'fromLocationName') {
      const fromLoc = newState['data']['locations'].find(x => x['locationName'] === newValue);
      newState['data']['trips'][rowIdx]['fromLocationId'] = fromLoc ? fromLoc['UUID'] : null;
    }
    if (columnName === 'toLocationName') {
      const toLoc = newState['data']['locations'].find(x => x['locationName'] === newValue);
      newState['data']['trips'][rowIdx]['toLocationId'] = toLoc ? toLoc['UUID'] : null;
    }
  }
  
  //checks to see if any dropdown pick-lists will be impacted by this change.
  if (oldValue) {
    for (const [iterTableName,] of Object.entries(newState['data'])) {
      if (STATIC_TABLE_SETTINGS[iterTableName]) {
        const dropdownSources = STATIC_TABLE_SETTINGS[iterTableName]['dropdownSource']
        if (!dropdownSources) {continue}
        
        for (const [iterColumnName, dropdownSource] of Object.entries(dropdownSources)) {
          if (dropdownSource) {
            
            if (dropdownSource.tableName === tableName) {
              const dropdownColumn = dropdownSource['column'] 
              if (dropdownColumn === columnName) {
                
                const dataType = STATIC_TABLE_SETTINGS[iterTableName]['dataType'][iterColumnName]
                let matchIdcs;
                if (dataType === 'dropdown') {
                  matchIdcs = findAllEntriesInTable(newState['data'], iterTableName, [iterColumnName], [oldValue])
                } else {
                  matchIdcs = findAllEntriesInTable(newState['data'], iterTableName, [iterColumnName], [oldValue], ['in'])
                }
              
                matchIdcs.forEach((matchIdx, _) => {
                  if (!newState['data'][iterTableName][matchIdx][iterColumnName]) {}
                  else if (dataType === 'dropdown') {
                    newState['data'][iterTableName][matchIdx][iterColumnName] = newValue;
                  }

                  /*
                  A multi-select column holds a list, and only the renamed entry
                  in it is replaced.
                  */
                  else if (Array.isArray(newState['data'][iterTableName][matchIdx][iterColumnName])) {
                    const index = newState['data'][iterTableName][matchIdx][iterColumnName].indexOf(oldValue);
                    newState['data'][iterTableName][matchIdx][iterColumnName].splice(index, 1);
                    newState['data'][iterTableName][matchIdx][iterColumnName].splice(index, 0, newValue)
                  }
                  /*
                  A single value that is not declared a 'dropdown' still needs the
                  new name. `travel_costs.vehicleType` is the case: it is typed
                  'text', so it fell through to the list branch above and renaming
                  a vehicle type threw "splice is not a function" on the first row
                  carrying that name. It only became reachable now that rows are
                  given their type's display name on load, but the branch was
                  always wrong for a scalar.

                  The equality test is the whole point of this branch. `matchIdcs`
                  was gathered with the 'in' matcher, which on a string is a
                  SUBSTRING match (inputFunctions.columnMatches). With vehicle
                  types "Van" and "Cargo Van", renaming "Van" to "Van XL" matches
                  the Cargo Van rows too — relabelling them would put a wrong name
                  on a read-only column that is now visible, with no way for anyone
                  to correct it. The ids are untouched either way, so this is
                  purely about what people are shown, which is reason enough.
                  */
                  else if (newState['data'][iterTableName][matchIdx][iterColumnName] === oldValue) {
                    newState['data'][iterTableName][matchIdx][iterColumnName] = newValue;
                  }
                  validateRow(newState, iterTableName, matchIdx);
                });
              } 
            }
          }
        }
      }
    }
  }

  if (STATIC_TABLE_SETTINGS[tableName]['forceUpdate']) {
    const forceUpdateSettings = STATIC_TABLE_SETTINGS[tableName]['forceUpdate'];
    for (const [, settings] of Object.entries(forceUpdateSettings)) {
      const correspondingColumn = settings['correspondingColumn'];
      const correspondingTable = settings['tableName'];
      const correspondingValue = item[correspondingColumn];
      const affectedRows = findAllEntriesInTable(newState['data'], correspondingTable, [correspondingColumn], [correspondingValue]);
      affectedRows.forEach((rowIdx) => {
        validateRow(newState, correspondingTable, rowIdx);
      })
    }
  }

  return newState;
}
